import { computed, unref } from 'vue';
import { useItemsGridData } from '@/composables/useItemsGridData';
import { useItemsGridReport } from '@/composables/useItemsGridReport';

/**
 * Composes the grid's data adapter and its report adapter into one bag a page
 * can spread straight onto the component:
 *
 *   const app  = useAppContext();
 *   const grid = useItemsGrid({ ...app, filterContext: app.filterItemsContext, t });
 *   <ItemsGrid v-bind="grid" />
 *
 * Everything in the returned object is an ItemsGrid prop, so `v-bind` alone is
 * the whole wiring.
 *
 * The bag is a `computed` of already-unwrapped values, NOT a `reactive()`
 * object: `reactive()` would proxy `filterContext` (a class instance) and
 * auto-unwrap its `filtersLastUpdated` field, changing what the component
 * deep-watches. A computed delivers each prop exactly as the old
 * `:filterContext="filterContext"` binding did — the raw value.
 *
 * Pass `report: false` for a page that should not offer the paginated report —
 * `generateReport` then stays null and the button is hidden.
 *
 * @param {object} deps  the useAppContext bag plus { filterContext, itemsView?, t, report? }
 */
export function useItemsGrid({
  conn,
  settings,
  logoUrl,
  apiHost,
  projectCode,
  statusTemplates,
  itemTemplates,
  categories,
  userId,
  dataLoaded,
  filterContext,
  itemsView = 'filteredItems',
  t,
  trackEvent,
  report = true
}) {
  // Created once, so their identity is stable across re-renders. `fetchPage` loads the user
  // lookup itself before every page (ItemsGrid used to call a `loadUsers` prop first as well —
  // the second call was a no-op; gone since the grid runs on DuckDbDataTable, IA 13.3b).
  const { users, fetchPage } = useItemsGridData({ conn, itemsView });

  const reportAdapter = report
    ? useItemsGridReport({ conn, settings, logoUrl, t, trackEvent })
    : null;

  // `buildMode` is a constant ('webapp') and $launchUrl does not exist in this
  // repo, so the production branch it used to guard is gone for good (R6)
  const openUrl = (url) => window.open(url, '_blank');

  return computed(() => ({
    filterContext: unref(filterContext),
    itemsView: unref(itemsView),
    dataLoaded: !!unref(dataLoaded),
    apiHost: unref(apiHost) || '',
    projectCode: unref(projectCode) || '',
    statusTemplates: unref(statusTemplates) || {},
    itemTemplates: unref(itemTemplates) || {},
    categories: unref(categories) || {},
    currentUserId: unref(userId) || '',
    users: unref(users) || {},
    fetchPage,
    openUrl,
    generateReport: reportAdapter ? reportAdapter.generateReport : null,
    reportBusy: reportAdapter ? unref(reportAdapter.generating) : false
  }));
}
